import React, { useState, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '../components/ui/card';
import {
  Star, MapPin, Languages, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import VideoGallery from '../components/VideoGallery';
import { ActivityCard } from './Activities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuideDetailSimple() {
  useCurrency(); // re-render quand la devise d'affichage change
  const { guideId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuide();
  }, [guideId]);

  const fetchGuide = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guides/${guideId}`);
      setGuide(response.data);
    } catch (error) {
      toast.error(t('guideDetail.notFound'));
      navigate('/hosts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  if (!guide) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="guide-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Hero Section */}
        <div className="mb-12">
          <div>
            {/* Bandeau de couverture : photo choisie librement par le guide
                (paysage japonais, vue de Tokyo...), indépendante de son avatar. */}
            <div className="relative w-full aspect-[16/9] sm:aspect-[3/1] rounded-2xl overflow-hidden bg-zinc-900 border border-white/10">
              {guide.cover_photo_url ? (
                <img src={guide.cover_photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#F9004D]/20 via-zinc-900 to-[#00F0FF]/20" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-end px-2 sm:px-6">
              {/* Avatar : remonté sur le bandeau, façon fiche de profil. */}
              <div className={'w-40 sm:w-56 shrink-0 aspect-square rounded-full overflow-hidden bg-zinc-900 border-4 shadow-2xl -mt-20 sm:-mt-24 relative z-10 '
                + (guide.featured ? 'border-[#D4AF37]' : 'border-[#050505]')}>
                <img
                  src={guide.profile_photo_url || guide.user_picture || 'https://images.unsplash.com/photo-1578656161729-a8f77b7ffd4c?w=800'}
                  alt={guide.user_name}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="min-w-0 pt-1 sm:pb-2">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {guide.user_name}
                  </h1>
                  {guide.featured && (
                    <span className="px-3 py-1 rounded-full bg-[#D4AF37] text-black text-xs font-bold">
                      {guide.featured === 'creator' ? t('host.featuredCreator') : t('host.featuredStar')}
                    </span>
                  )}
                </div>

                {guide.average_rating > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="h-5 w-5 fill-[#D4AF37] text-[#D4AF37]" />
                    <span className="text-white font-bold text-lg">{guide.average_rating.toFixed(1)}</span>
                    <span className="text-zinc-400 text-sm">({guide.total_reviews} {t('activityDetail.reviewsWord')})</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {guide.areas?.map((area) => (
                    <span key={area} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-white flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Bio */}
            <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6">
              <h2 className="text-xl font-bold text-white mb-4">{t('guideDetail.about')}</h2>
              <p className="text-zinc-300 leading-relaxed">
                {guide.bio?.[i18n.language] || guide.bio?.fr || guide.bio?.en || t('guideDetail.bioFallback')}
              </p>
              
              {/* Languages */}
              <div className="mt-6">
                <h3 className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                  <Languages className="h-4 w-4" /> {t('activityDetail.spokenLanguages')}
                </h3>
                {guide.language_levels && Object.keys(guide.language_levels).some(k => guide.language_levels[k] > 0) ? (
                  <div className="space-y-2">
                    {[
                      { key: 'en', label: t('activityDetail.langEn') },
                      { key: 'fr', label: t('activityDetail.langFr') },
                      { key: 'jp', label: t('activityDetail.langJp') }
                    ].filter(({ key }) => guide.language_levels[key] > 0).map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="text-zinc-300 text-sm w-28">{label}</span>
                        <div className="flex gap-1">
                          {[1,2,3,4,5,6,7].map(n => (
                            <div key={n} className={'w-4 h-4 rounded-full ' + (guide.language_levels[key] >= n ? 'bg-[#F9004D]' : 'bg-white/10')} />
                          ))}
                        </div>
                        <span className="text-zinc-500 text-xs">{guide.language_levels[key]}/7</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {guide.languages_spoken?.map((lang) => (
                      <span key={lang} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-zinc-300 uppercase">
                        {lang}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Specialties */}
              <div className="mt-6">
                <h3 className="text-zinc-400 text-sm mb-2">{t('guideDetail.specialties')}</h3>
                <div className="flex flex-wrap gap-2">
                  {guide.specialties?.map((spec) => (
                    <span key={spec} className="px-3 py-1 bg-[#F9004D]/10 border border-[#F9004D]/30 rounded-full text-sm text-[#F9004D] capitalize">
                      {spec === 'food' && t('guideDetail.spec.food')}
                      {spec === 'culture' && t('guideDetail.spec.culture')}
                      {spec === 'nightlife' && t('guideDetail.spec.nightlife')}
                      {spec === 'shopping' && t('guideDetail.spec.shopping')}
                      {spec === 'nature' && t('guideDetail.spec.nature')}
                      {!['food', 'culture', 'nightlife', 'shopping', 'nature'].includes(spec) && spec}
                    </span>
                  ))}
                </div>
              </div>
            </Card>

            {/* Experiences */}
            <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6">
              <h2 className="text-xl font-bold text-white mb-4">{t('guideDetail.experiencesOffered')}</h2>

              {guide.activities?.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {guide.activities.map((act) => (
                    <ActivityCard
                      key={act.activity_id}
                      activity={{
                        ...act,
                        guide_name: guide.user_name,
                        guide_picture: guide.profile_photo_url || guide.user_picture,
                        guide_languages: guide.languages_spoken
                      }}
                      onClick={() => navigate('/activities/' + act.activity_id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">{t('guideDetail.noActivities')}</p>
              )}
            </Card>

            <VideoGallery scope="guide" id={guideId} title={t('activityDetail.travelerVideos')} />

            {/* Reviews */}
            {guide.reviews?.length > 0 && (
              <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6">
                <h2 className="text-xl font-bold text-white mb-4">
                  {t('guideDetail.reviewsCount', { count: guide.reviews.length })}
                </h2>
                <div className="space-y-4">
                  {guide.reviews.slice(0, 5).map((review) => (
                    <div key={review.review_id} className="border-b border-white/10 pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-600'}`}
                          />
                        ))}
                      </div>
                      <p className="text-zinc-300">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
